import { createHttpClient } from "@services/sdk/helpers/http-client";

import {
	createAWSAdapter,
	createContentfulAdapter,
	createMRTMAdapter,
	createPSMAAdapter,
} from "@services/sdk/adapters";

import type { IFelixClientConfig } from "@services/sdk/interfaces";
import ErrorResponse from "@services/sdk/models/error-response";
import { Result } from "./models";

export const defaultFelixClientConfig: IFelixClientConfig = {
	isConnected: true,
	baseURL: process.env.NEXT_PUBLIC_MRTM_BASE_URL || "",
	xServerSelect: process.env.NEXT_PUBLIC_MRTM_MODEL,
	requestTimeout: 20000,
	contentfulAccessToken: process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN,
	contentfulSpace: process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID,
	psmaBaseURL: process.env.NEXT_PUBLIC_PSMA_BASE_URL || "",
	psmaAPIKey: process.env.NEXT_PUBLIC_PSMA_API_KEY || "",
	awsBaseURL: process.env.NEXT_PUBLIC_AWS_BASE_URL || "",
	awsUser: process.env.NEXT_PUBLIC_AWS_AUTH_USER || "",
};

const client = (config: IFelixClientConfig = defaultFelixClientConfig) => {
	const mrtmHttpClient = createHttpClient({
		baseURL: config.baseURL,
		timeout: config.requestTimeout,
		headers: {
			"X-Server-Select": config.xServerSelect || "",
			...(config.mrtmAuthToken && {
				Authorization: `Bearer ${config.mrtmAuthToken}`,
			}),
		},
	});

	const MRTMAdapter = createMRTMAdapter({
		httpClient: mrtmHttpClient,
	});

	const contentful = require("contentful");

	const ContentfulAdapter = createContentfulAdapter(
		contentful.createClient({
			accessToken: `${process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN}`,
			space: `${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID}`,
		}),
	);

	const PSMAAdapter = createPSMAAdapter(
		createHttpClient({
			baseURL: config.psmaBaseURL,
			timeout: config.requestTimeout,
			headers: {
				"API-KEY": config.psmaAPIKey,
				Authorization: config.psmaAPIKey,
			},
		}),
	);

	const BinCheckAdapter = createAWSAdapter(
		createHttpClient({
			baseURL: config.awsBaseURL,
			timeout: config.requestTimeout,
		}),
		config.awsUser,
	);

	const idLookUpAdapter = createAWSAdapter(mrtmHttpClient, config.awsUser);

	const client = {
		config,
		mrtmHttpClient,
		getAddress: PSMAAdapter.getAddress,
		getAddressSuggestions: PSMAAdapter.getAddressSuggestions,
		getLegalContent: ContentfulAdapter.getLegalContent,
		getImpactPage: ContentfulAdapter.getImpactPage,
		getImpactRanges: ContentfulAdapter.getImpactRanges,
		getFAQPage: ContentfulAdapter.getFAQPage,
		getWelcomeCarouselSlides: ContentfulAdapter.getWelcomeCarouselSlides,
		getAsset: ContentfulAdapter.getAsset,
		getPaymentToken: MRTMAdapter.payment.getPaymentToken,
		getPlanSummary: MRTMAdapter.products.getPlanSummary,
		getProducts: MRTMAdapter.products.getProducts,
		onboard: MRTMAdapter.onboarding.onboard,
		verifyEmail: MRTMAdapter.auth.verifyEmail,
		verifyPhoneNumber: MRTMAdapter.porting.verifyPhoneNumber,
		verifyPromoCode: MRTMAdapter.campaigns.verifyPromoCode,
		awsIdLookUp: idLookUpAdapter.awsIdLookUp,
		binCheck: BinCheckAdapter.binCheck,
	};

	const handler = {
		get(obj: typeof client, prop: keyof typeof client) {
			if (!config.isConnected) {
				if (prop === "config") {
					return obj.config;
				}

				return () =>
					new Promise((res) =>
						res(Result.Failure(ErrorResponse.NetworkError())),
					);
			}

			return obj[prop];
		},
	};

	return new Proxy<typeof client>(client, handler);
};

export default client;
